import React, { useEffect } from "react";
import { Icon, IconButton, SectionLabel } from "../ui";

/* SourcePicker — "what are you bringing in?" modal, ported from
 * oab-v1-design/components/source-picker.jsx. Actionable sources deep-link into
 * the corresponding legacy tool (via /tools?tab=); the rest are shown dimmed
 * until their dedicated V0.2 staging flows exist. */

export type SourceKind =
  | "xlsx" | "gis" | "dxf" | "pdf" | "pavement" | "corridor"
  | "alignment" | "ifc" | "surface" | "photo" | "template" | "blank";

// Source kind -> dedicated V0.2 route (a surface already rebuilt out of the
// legacy tab into its own route during Phase E). Checked before TOOL_TAB.
const PORTED_ROUTE: Partial<Record<SourceKind, string>> = {
  gis: "/gis",
  dxf: "/cad",
  pdf: "/staging",
  xlsx: "/builder",
  pavement: "/pavement",
  corridor: "/corridor",
  photo: "/photo",
};

// Source kind -> legacy tool tab id (consumed by LegacyTools' ?tab= param).
// (Empty — every tool-backed source now has a dedicated route.)
const TOOL_TAB: Partial<Record<SourceKind, string>> = {};

/** Returns the deep-link for a source kind, or null if not tool-backed.
 *  Prefers a dedicated rebuilt route, falling back to the legacy /tools tab. */
export function sourceToToolPath(kind: SourceKind): string | null {
  if (PORTED_ROUTE[kind]) return PORTED_ROUTE[kind] as string;
  const tab = TOOL_TAB[kind];
  return tab ? `/tools?tab=${tab}` : null;
}

interface TileDef {
  kind: SourceKind;
  icon: string;
  title: string;
  subtitle: string;
  group?: string;
}

const SPATIAL: TileDef[] = [
  { kind: "alignment", icon: "alignment", title: "From Alignment", subtitle: "LandXML horizontal alignment", group: "LandXML-drape" },
  { kind: "ifc", icon: "cube", title: "From IFC corridor", subtitle: "Pre-flight, decompose into sources", group: "Doctor-OAB" },
  { kind: "surface", icon: "surface", title: "From Surface", subtitle: "TIN from IFC / LandXML / OBJ", group: "Surfer" },
];
const ASSET: TileDef[] = [
  { kind: "xlsx", icon: "spreadsheet", title: "From Spreadsheet", subtitle: "Excel / CSV", group: "Asset Builder" },
  { kind: "gis", icon: "map", title: "From GIS", subtitle: "Draw a polygon over a REST source", group: "GIS Import" },
  { kind: "dxf", icon: "cad", title: "From DXF / CAD", subtitle: "Layer classification", group: "CAD-to-BIM" },
  { kind: "pdf", icon: "pdf", title: "From PDF plans", subtitle: "Calibrate & place on sheets", group: "PDF Staging" },
  { kind: "pavement", icon: "layers", title: "From Pavement", subtitle: "Footprint × design × surface", group: "Pavement" },
  { kind: "corridor", icon: "alignment", title: "From corridor", subtitle: "Markings · walls · guardrail · fencing", group: "DXF-first" },
  { kind: "photo", icon: "photo", title: "From Field Photos", subtitle: "Capture on your phone → ledger", group: "Field Capture" },
];
const FRESH: TileDef[] = [
  { kind: "template", icon: "template", title: "From Template", subtitle: "FDOT, GDOT, NCDOT, TDOT starter packs" },
  { kind: "blank", icon: "blank", title: "Blank project", subtitle: "Create an empty project" },
];

const Tile: React.FC<{ t: TileDef; dim?: boolean; onClick?: () => void }> = ({ t, dim, onClick }) => (
  <button
    onClick={dim ? undefined : onClick}
    disabled={dim}
    title={dim ? "Dedicated staging flow coming soon" : undefined}
    style={{
      display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8,
      padding: "16px 16px 18px", background: "var(--bg-surface)",
      border: "1px solid var(--border-subtle)", borderRadius: "var(--r-lg)",
      textAlign: "left", transition: "all 140ms", position: "relative", overflow: "hidden",
      opacity: dim ? 0.5 : 1, cursor: dim ? "not-allowed" : "pointer",
    }}
    onMouseEnter={(e) => { if (!dim) { e.currentTarget.style.borderColor = "var(--accent-border)"; e.currentTarget.style.background = "var(--accent-soft)"; } }}
    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--border-subtle)"; e.currentTarget.style.background = "var(--bg-surface)"; }}
  >
    <div style={{ width: 32, height: 32, borderRadius: "var(--r-sm)", background: "var(--bg-surface-2)", color: "var(--fg-primary)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Icon name={t.icon} size={18} />
    </div>
    <div>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--fg-primary)", marginBottom: 2, letterSpacing: "-0.01em" }}>{t.title}</div>
      <div style={{ fontSize: 11.5, color: "var(--fg-tertiary)", lineHeight: 1.4 }}>{t.subtitle}</div>
    </div>
    {t.group && <span className="mono" style={{ position: "absolute", top: 12, right: 12, fontSize: 9, color: "var(--fg-muted)", letterSpacing: "0.06em", textTransform: "uppercase" }}>{t.group}</span>}
  </button>
);

interface Props {
  open: boolean;
  onClose: () => void;
  onPick: (kind: SourceKind) => void;
}

export default function SourcePicker({ open, onClose, onPick }: Props) {
  // Esc closes the dialog. Hook runs unconditionally (before the early return)
  // to satisfy rules-of-hooks; it no-ops while closed.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const actionable = (k: SourceKind) => k === "blank" || sourceToToolPath(k) != null;
  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 200, background: "oklch(0% 0 0 / 0.45)", backdropFilter: "blur(6px)", display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 72, paddingBottom: 40, overflowY: "auto" }}
    >
      <div role="dialog" aria-modal="true" aria-label="Start a new project" onClick={(e) => e.stopPropagation()} style={{ width: 880, maxWidth: "calc(100vw - 32px)", background: "var(--bg-surface)", border: "1px solid var(--border-subtle)", borderRadius: "var(--r-xl)", boxShadow: "var(--shadow-pop)", overflow: "hidden" }}>
        <div style={{ padding: "22px 28px 18px", borderBottom: "1px solid var(--border-subtle)", display: "flex", alignItems: "center", gap: 14 }}>
          <div>
            <div className="mono" style={{ fontSize: 10.5, color: "var(--fg-tertiary)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>Start new project</div>
            <h2 style={{ fontSize: 19, fontWeight: 600, letterSpacing: "-0.018em", margin: 0 }}>What are you bringing in?</h2>
          </div>
          <span style={{ flex: 1 }} />
          <IconButton icon="x" onClick={onClose} title="Close" />
        </div>

        <div style={{ padding: "20px 28px 28px" }}>
          <SectionLabel style={{ marginBottom: 10 }}>Spatial — defines where</SectionLabel>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 22 }}>
            {SPATIAL.map((t) => <Tile key={t.kind} t={t} dim={!actionable(t.kind)} onClick={() => onPick(t.kind)} />)}
          </div>

          <SectionLabel style={{ marginBottom: 10 }}>Asset — defines what's in</SectionLabel>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 22 }}>
            {ASSET.map((t) => <Tile key={t.kind} t={t} dim={!actionable(t.kind)} onClick={() => onPick(t.kind)} />)}
          </div>

          <SectionLabel style={{ marginBottom: 10 }}>Or start fresh</SectionLabel>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
            {FRESH.map((t) => <Tile key={t.kind} t={t} dim={!actionable(t.kind)} onClick={() => onPick(t.kind)} />)}
          </div>

          <div style={{ marginTop: 22, padding: "14px 16px", background: "var(--bg-surface-2)", border: "1px dashed var(--border-default)", borderRadius: "var(--r-md)", display: "flex", alignItems: "center", gap: 12, fontSize: 12.5, color: "var(--fg-tertiary)" }}>
            <Icon name="upload" size={16} color="var(--fg-tertiary)" />
            <span>Drag-and-drop staging arrives in a later step. For now, pick a tool source above.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
